import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { listPayload } from '@/lib/payload';
import { formatCurrencyAmount } from '@/lib/formatMoney';
import { getApiErrorMessage } from '@/stores/partnerAuthStore';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FollowUpTable } from '@/components/tables/FollowUpTable';

interface Officer {
  name: string;
  role: string;
  phone: string;
  email: string;
}

interface PendingPaymentGroup {
  groupId: number;
  groupName: string;
  officers: Officer[];
  invoiceSentAt: string | null;
  daysPending: number | null;
}

interface FailedPayment {
  paymentId: number;
  groupName: string;
  amount: number;
  currency: string;
  status: string;
  failureReason: string | null;
  initiator: { name: string; phone: string; email: string };
  createdAt: string;
}

interface TrialExpiring {
  groupId: number;
  groupName: string;
  trialEndsAt: string;
  daysRemaining: number;
  officers: Officer[];
}

interface OverdueInvoice {
  invoiceId: number;
  groupName: string;
  amount: number;
  currency: string;
  dueAt: string;
  daysOverdue: number;
}

function officerContact(officers: Officer[]): string {
  const primary = officers[0];
  return primary ? `${primary.name} (${primary.phone})` : 'No officer on record';
}

export function FollowUpDashboardPage() {
  const [pendingPayment, setPendingPayment] = useState<PendingPaymentGroup[]>([]);
  const [failedPayments, setFailedPayments] = useState<FailedPayment[]>([]);
  const [trialsExpiring, setTrialsExpiring] = useState<TrialExpiring[]>([]);
  const [overdueInvoices, setOverdueInvoices] = useState<OverdueInvoice[]>([]);

  useEffect(() => {
    // Without these catches a failed request was an unhandled rejection and
    // the tab still read "No overdue invoices.", which is indistinguishable
    // from a healthy empty result. One toast id per page so a shared failure
    // raises one toast rather than four identical ones.
    const reportFailed = (error: unknown) =>
      toast.error(getApiErrorMessage(error), { id: 'follow-up-report-error' });

    void api.get('/reports/follow-up/pending-payment-groups').then((res) => setPendingPayment(listPayload(res))).catch((error) => { setPendingPayment([]); reportFailed(error); });
    void api.get('/reports/follow-up/failed-payments?days=30').then((res) => setFailedPayments(listPayload(res))).catch((error) => { setFailedPayments([]); reportFailed(error); });
    void api.get('/reports/follow-up/trials-expiring?days=14').then((res) => setTrialsExpiring(listPayload(res))).catch((error) => { setTrialsExpiring([]); reportFailed(error); });
    void api.get('/reports/follow-up/overdue-invoices').then((res) => setOverdueInvoices(listPayload(res))).catch((error) => { setOverdueInvoices([]); reportFailed(error); });
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold">Follow-up</h1>
        <p className="text-muted-foreground">Groups and invoices that need attention.</p>
      </div>
      <Card>
        <CardContent className="pt-6">
          <Tabs defaultValue="pending-payment">
            <TabsList>
              <TabsTrigger value="pending-payment">Pending payment</TabsTrigger>
              <TabsTrigger value="failed-payments">Failed payments</TabsTrigger>
              <TabsTrigger value="trials-expiring">Trials expiring</TabsTrigger>
              <TabsTrigger value="overdue-invoices">Overdue invoices</TabsTrigger>
            </TabsList>
            <TabsContent value="pending-payment">
              <FollowUpTable
                rows={pendingPayment}
                emptyMessage="No groups awaiting payment confirmation."
                columns={[
                  { key: 'group', label: 'Group', render: (r) => r.groupName },
                  { key: 'officer', label: 'Officer', render: (r) => officerContact(r.officers) },
                  { key: 'days', label: 'Days pending', render: (r) => r.daysPending ?? 'N/A' },
                ]}
              />
            </TabsContent>
            <TabsContent value="failed-payments">
              <FollowUpTable
                rows={failedPayments}
                emptyMessage="No failed payments in the last 30 days."
                columns={[
                  { key: 'group', label: 'Group', render: (r) => r.groupName },
                  { key: 'amount', label: 'Amount', render: (r) => formatCurrencyAmount(r.amount, r.currency) },
                  { key: 'reason', label: 'Reason', render: (r) => r.failureReason ?? 'Unknown' },
                  { key: 'initiator', label: 'Initiator', render: (r) => `${r.initiator.name} (${r.initiator.phone})` },
                ]}
              />
            </TabsContent>
            <TabsContent value="trials-expiring">
              <FollowUpTable
                rows={trialsExpiring}
                emptyMessage="No trials expiring in the next 14 days."
                columns={[
                  { key: 'group', label: 'Group', render: (r) => r.groupName },
                  { key: 'officer', label: 'Officer', render: (r) => officerContact(r.officers) },
                  { key: 'days', label: 'Days remaining', render: (r) => r.daysRemaining },
                ]}
              />
            </TabsContent>
            <TabsContent value="overdue-invoices">
              <FollowUpTable
                rows={overdueInvoices}
                emptyMessage="No overdue invoices."
                columns={[
                  { key: 'group', label: 'Group', render: (r) => r.groupName },
                  { key: 'amount', label: 'Amount', render: (r) => formatCurrencyAmount(r.amount, r.currency) },
                  { key: 'days', label: 'Days overdue', render: (r) => r.daysOverdue },
                ]}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
